import { http } from '~/src/services/requests';
import qs from 'qs';
import {
  ClioApiResponse,
  ClioDocument,
  ClioFolder,
  ClioDocumentTemplate,
  DocumentSearchParams,
  FolderSearchParams,
  DocumentTemplateSearchParams,
  DocumentTemplateSearchQuery,
  BreadcrumbItem,
  ClioApiResponseMatterRootFolder,
} from '~/src/types/clioDocumentPicker';
import { DEFAULT_DOCUMENT_CONTENT_TYPES } from '~/src/constants/documentMimeTypes';

interface DocumentApiParams {
  limit: string;
  q?: string;
  page_token?: string;
  matter_id?: string;
  parent_id?: string;
  name?: string;
  created_since?: string;
  updated_since?: string;
  only_fully_uploaded?: string;
  days?: string;
  content_types?: string[];
}

interface DocumentTemplateApiParams {
  limit: string;
  query?: string;
  page_token?: string;
  name?: string;
  created_since?: string;
  updated_since?: string;
  content_types?: string[];
}

interface FolderApiParams {
  limit: string;
  parent_id?: string;
  matter_id?: string;
  page_token?: string;
}

// Helper function for building folder URLs according to API spec
function buildFolderUrl(
  orgFprint: string,
  options: {
    limit?: number;
    parentId?: number | null;
    matterId?: number;
    pageToken?: string;
  } = {},
): string {
  const { limit = 50, parentId, matterId, pageToken } = options;

  const params: FolderApiParams = {
    limit: limit.toString(),
  };

  // CRITICAL: Only add parent_id if it's a real folder ID (not null/0)
  if (parentId && parentId !== 0) {
    params.parent_id = parentId.toString();
  }

  if (matterId) params.matter_id = matterId.toString();
  if (pageToken) params.page_token = pageToken;

  const queryString = qs.stringify(params);
  return `/document/api/v1/org/${orgFprint}/clio/folders/?${queryString}`;
}

// Helper function for building document URLs according to API spec
function buildDocumentUrl(
  orgFprint: string,
  endpoint: string,
  options: {
    limit?: number;
    parentId?: number | null;
    matterId?: number;
    pageToken?: string;
    query?: string;
    name?: string;
    createdSince?: string;
    updatedSince?: string;
    onlyFullyUploaded?: boolean;
    days?: number;
    contentTypes?: string[];
  } = {},
): string {
  const {
    limit = 50,
    parentId,
    matterId,
    pageToken,
    query,
    name,
    createdSince,
    updatedSince,
    onlyFullyUploaded,
    days,
    contentTypes,
  } = options;

  const params: DocumentApiParams = {
    limit: Math.min(limit, 200).toString(), // Max 200 as per spec
  };

  if (query) params.q = query.trim();
  if (pageToken) params.page_token = pageToken;
  if (matterId) params.matter_id = matterId.toString();
  if (parentId) params.parent_id = parentId.toString();
  if (name) params.name = name;
  if (createdSince) params.created_since = createdSince;
  if (updatedSince) params.updated_since = updatedSince;
  if (onlyFullyUploaded !== undefined)
    params.only_fully_uploaded = onlyFullyUploaded.toString();
  if (days) params.days = days.toString();
  if (contentTypes && contentTypes.length > 0)
    params.content_types = contentTypes;

  const queryString = qs.stringify(params);
  return `/document/api/v1/org/${orgFprint}/clio/documents/${endpoint}?${queryString}`;
}

// Helper function for building document template URLs according to API spec
function buildDocumentTemplateUrl(
  orgFprint: string,
  endpoint: string,
  options: {
    limit?: number;
    pageToken?: string;
    query?: string;
    name?: string;
    createdSince?: string;
    updatedSince?: string;
    contentTypes?: string[];
  } = {},
): string {
  const {
    limit = 50,
    pageToken,
    query,
    name,
    createdSince,
    updatedSince,
    contentTypes,
  } = options;

  const params: DocumentTemplateApiParams = {
    limit: Math.min(limit, 200).toString(), // Max 200 as per spec
  };

  if (query) params.query = query.trim();
  if (pageToken) params.page_token = pageToken;
  if (name) params.name = name;
  if (createdSince) params.created_since = createdSince;
  if (updatedSince) params.updated_since = updatedSince;
  if (contentTypes && contentTypes.length > 0)
    params.content_types = contentTypes;

  const queryString = qs.stringify(params);
  return `/document/api/v1/org/${orgFprint}/clio/document-templates/${endpoint}?${queryString}`;
}

// Enhanced error handling for authentication issues
function handleApiError(error: any, _endpoint: string): never {
  if (error.response?.status === 401) {
    throw new Error('Unable to connect to Clio Manage.');
  }
  if (error.response?.status === 403) {
    throw new Error(
      "You don't have permission to access this Clio Manage resource.",
    );
  }
  throw error;
}

function clioDocumentPickerService() {
  const baseUrl = (orgFprint: string) =>
    `/document/api/v1/org/${orgFprint}/clio`;

  return {
    /**
     * List documents with optional filtering and pagination
     * Conforms to API spec: GET /document/api/v1/org/{orgFprint}/clio/documents/
     * Defaults to Word documents (.docx) if no content_types specified
     */
    listDocuments: async (
      orgFprint: string,
      params: DocumentSearchParams = {},
    ): Promise<ClioApiResponse<ClioDocument>> => {
      try {
        const url = buildDocumentUrl(orgFprint, '', {
          limit: params.limit || 50,
          parentId: params.parent_id,
          matterId: params.matter_id,
          pageToken: params.page_token,
          name: params.name,
          createdSince: params.created_since,
          updatedSince: params.updated_since,
          onlyFullyUploaded: params.only_fully_uploaded ?? true,
          contentTypes: params.content_types || DEFAULT_DOCUMENT_CONTENT_TYPES,
        });

        const parse = true;
        const response = await http.innerSendAuthenticated(
          {
            relativePath: url,
            method: 'GET',
          },
          parse,
        );

        // Add source field to documents and normalize the data structure
        if (response.documents) {
          response.documents = response.documents.map((doc: any) => ({
            ...doc,
            // Handle null filename by providing a fallback
            filename: doc.filename || 'Untitled Document',
            // Map size from latest_document_version to match our interface
            size: doc.latest_document_version?.size || 0,
            // Ensure content_type exists
            content_type:
              doc.content_type ||
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            source: 'document' as const,
          }));
        }

        return response;
      } catch (error) {
        return handleApiError(error, 'listDocuments');
      }
    },

    /**
     * Search documents by query string
     * Conforms to API spec: GET /document/api/v1/org/{orgFprint}/clio/documents/search/
     * REQUIRED: q parameter must be provided
     * Defaults to Word documents (.docx) if no content_types specified
     */
    searchDocuments: async (
      orgFprint: string,
      query: string,
      params: Omit<DocumentSearchParams, 'q'> = {},
    ): Promise<ClioApiResponse<ClioDocument>> => {
      if (!query.trim()) {
        throw new Error('Search query is required');
      }

      try {
        const url = buildDocumentUrl(orgFprint, 'search/', {
          limit: params.limit || 50,
          parentId: params.parent_id,
          matterId: params.matter_id,
          pageToken: params.page_token,
          query: query.trim(),
          contentTypes: params.content_types || DEFAULT_DOCUMENT_CONTENT_TYPES,
        });

        const parse = true;
        const response = await http.innerSendAuthenticated(
          {
            relativePath: url,
            method: 'GET',
          },
          parse,
        );

        // Add source field to documents and normalize the data structure
        if (response.documents) {
          response.documents = response.documents.map((doc: any) => ({
            ...doc,
            // Handle null filename by providing a fallback
            filename: doc.filename || 'Untitled Document',
            // Map size from latest_document_version to match our interface
            size: doc.latest_document_version?.size || 0,
            // Ensure content_type exists
            content_type:
              doc.content_type ||
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            source: 'document' as const,
          }));
        }

        return response;
      } catch (error) {
        return handleApiError(error, 'searchDocuments');
      }
    },

    /**
     * Get document details by ID
     * Conforms to API spec: GET /document/api/v1/org/{orgFprint}/clio/documents/{documentId}/
     */
    getDocument: async (
      orgFprint: string,
      documentId: number,
    ): Promise<ClioDocument> => {
      try {
        const parse = true;
        return await http.innerSendAuthenticated(
          {
            relativePath: `${baseUrl(orgFprint)}/documents/${documentId}/`,
            method: 'GET',
          },
          parse,
        );
      } catch (error) {
        return handleApiError(error, 'getDocument');
      }
    },

    /**
     * Get recent documents
     * Conforms to API spec: GET /document/api/v1/org/{orgFprint}/clio/documents/recent/
     * Default: limit=20, days=30
     * Defaults to Word documents (.docx)
     */
    getRecentDocuments: async (
      orgFprint: string,
      limit: number = 20,
      days: number = 30,
      contentTypes?: string[],
    ): Promise<ClioApiResponse<ClioDocument>> => {
      try {
        const url = buildDocumentUrl(orgFprint, 'recent/', {
          limit,
          days,
          contentTypes: contentTypes || DEFAULT_DOCUMENT_CONTENT_TYPES,
        });

        const parse = true;
        const response = await http.innerSendAuthenticated(
          {
            relativePath: url,
            method: 'GET',
          },
          parse,
        );

        // Add source field to documents and normalize the data structure
        if (response.documents) {
          response.documents = response.documents.map((doc: any) => ({
            ...doc,
            // Handle null filename by providing a fallback
            filename: doc.filename || 'Untitled Document',
            // Map size from latest_document_version to match our interface
            size: doc.latest_document_version?.size || 0,
            // Ensure content_type exists
            content_type:
              doc.content_type ||
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            source: 'document' as const,
          }));
        }

        return response;
      } catch (error) {
        return handleApiError(error, 'getRecentDocuments');
      }
    },

    /**
     * Download document (returns download URL or blob)
     * Conforms to API spec: GET /document/api/v1/org/{orgFprint}/clio/documents/{documentId}/download/
     */
    downloadDocument: async (
      orgFprint: string,
      documentId: number,
    ): Promise<Response> => {
      try {
        const parse = false;
        return await http.innerSendAuthenticated(
          {
            relativePath: `${baseUrl(
              orgFprint,
            )}/documents/${documentId}/download/`,
            method: 'GET',
          },
          parse,
        );
      } catch (error) {
        return handleApiError(error, 'downloadDocument');
      }
    },

    /**
     * Download document template (returns download URL or blob)
     * Conforms to API spec: GET /document/api/v1/org/{orgFprint}/clio/document-templates/{templateId}/download/
     */
    downloadDocumentTemplate: async (
      orgFprint: string,
      templateId: number,
    ): Promise<Response> => {
      try {
        const parse = false;
        return await http.innerSendAuthenticated(
          {
            relativePath: `${baseUrl(
              orgFprint,
            )}/document-templates/${templateId}/download/`,
            method: 'GET',
          },
          parse,
        );
      } catch (error) {
        return handleApiError(error, 'downloadDocumentTemplate');
      }
    },

    /**
     * List document templates with optional filtering and pagination
     * Conforms to API spec: GET /api/v1/org/{orgFprint}/clio/document-templates/
     * Defaults to Word documents (.docx) if no content_types specified
     */
    listDocumentTemplates: async (
      orgFprint: string,
      params: DocumentTemplateSearchParams = {},
    ): Promise<ClioApiResponse<ClioDocumentTemplate>> => {
      try {
        const url = buildDocumentTemplateUrl(orgFprint, '', {
          limit: params.limit || 50,
          pageToken: params.page_token,
          name: params.name,
          createdSince: params.created_since,
          updatedSince: params.updated_since,
          contentTypes: params.content_types || DEFAULT_DOCUMENT_CONTENT_TYPES,
        });

        const parse = true;
        const response = await http.innerSendAuthenticated(
          {
            relativePath: url,
            method: 'GET',
          },
          parse,
        );

        // Add source field to templates
        if (response.document_templates) {
          response.document_templates = response.document_templates.map(
            (template: any) => ({
              ...template,
              source: 'template' as const,
            }),
          );
        }

        return response;
      } catch (error) {
        return handleApiError(error, 'listDocumentTemplates');
      }
    },

    /**
     * Search document templates by query string
     * Conforms to API spec: GET /api/v1/org/{orgFprint}/clio/document-templates/search/
     * REQUIRED: query parameter must be provided
     * Defaults to Word documents (.docx) if no content_types specified
     */
    searchDocumentTemplates: async (
      orgFprint: string,
      searchParams: DocumentTemplateSearchQuery,
    ): Promise<ClioApiResponse<ClioDocumentTemplate>> => {
      if (!searchParams.query.trim()) {
        throw new Error('Search query is required');
      }

      try {
        const url = buildDocumentTemplateUrl(orgFprint, 'search/', {
          limit: searchParams.limit || 50,
          pageToken: searchParams.page_token,
          query: searchParams.query.trim(),
          contentTypes:
            searchParams.content_types || DEFAULT_DOCUMENT_CONTENT_TYPES,
        });

        const parse = true;
        const response = await http.innerSendAuthenticated(
          {
            relativePath: url,
            method: 'GET',
          },
          parse,
        );

        // Add source field to templates
        if (response.document_templates) {
          response.document_templates = response.document_templates.map(
            (template: any) => ({
              ...template,
              source: 'template' as const,
            }),
          );
        }

        return response;
      } catch (error) {
        return handleApiError(error, 'searchDocumentTemplates');
      }
    },

    /**
     * List folders with optional filtering and pagination
     * Conforms to API spec: GET /document/api/v1/org/{orgFprint}/clio/folders/
     * CRITICAL: For root folders, omit parent_id parameter entirely (not parent_id=0)
     */
    listFolders: async (
      orgFprint: string,
      params: FolderSearchParams = {},
    ): Promise<ClioApiResponse<ClioFolder>> => {
      try {
        const url = buildFolderUrl(orgFprint, {
          limit: params.limit || 50,
          parentId: params.parent_id,
          matterId: params.matter_id,
          pageToken: params.page_token,
        });

        const parse = true;
        return await http.innerSendAuthenticated(
          {
            relativePath: url,
            method: 'GET',
          },
          parse,
        );
      } catch (error) {
        return handleApiError(error, 'listFolders');
      }
    },

    /**
     * Get folder breadcrumb trail
     * Conforms to API spec: GET /document/api/v1/org/{orgFprint}/clio/folders/{folderId}/breadcrumb/
     */
    getFolderBreadcrumb: async (
      orgFprint: string,
      folderId: number,
    ): Promise<BreadcrumbItem[]> => {
      try {
        const parse = true;
        const response = await http.innerSendAuthenticated(
          {
            relativePath: `${baseUrl(
              orgFprint,
            )}/folders/${folderId}/breadcrumb/`,
            method: 'GET',
          },
          parse,
        );

        return response.breadcrumbs;
      } catch (error) {
        return handleApiError(error, 'getFolderBreadcrumb');
      }
    },

    getMatterFolder: async (
      orgFprint: string,
      matter_id: string,
    ): Promise<ClioApiResponseMatterRootFolder> => {
      try {
        const url = `${baseUrl(orgFprint)}/matter/${matter_id}/folder/`;

        const parse = true;
        const response = await http.innerSendAuthenticated(
          {
            relativePath: url,
            method: 'GET',
          },
          parse,
        );
        return response;
      } catch (error: any) {
        return handleApiError(error, 'getMatterFolder');
      }
    },

    /**
     * Get combined folder and document listing for a specific folder
     * This is a convenience method that fetches folders, documents, and breadcrumbs in parallel
     * For root folders (folderId is null), omits parent_id parameter as per API spec
     */
    getFolderContents: async (
      orgFprint: string,
      folderId: number | null = null,
      params: {
        page_token?: string;
        limit?: number;
        matter_id?: number;
        content_types?: string[];
      } = {},
    ): Promise<{
      documents: ClioDocument[];
      folders: ClioFolder[];
      next_page_token?: string;
      breadcrumbs?: BreadcrumbItem[];
    }> => {
      try {
        // For root folders (folderId is null), don't include parent_id
        const folderParams: FolderSearchParams = {
          ...params,
          ...(folderId && { parent_id: folderId }),
        };

        const documentParams: DocumentSearchParams = {
          ...params,
          ...(folderId && { parent_id: folderId }),
          only_fully_uploaded: true,
        };

        // Fetch folders and documents in parallel
        const service = clioDocumentPickerService();
        const [foldersResponse, documentsResponse] = await Promise.all([
          service.listFolders(orgFprint, folderParams),
          service.listDocuments(orgFprint, documentParams),
        ]);

        // Always fetch proper breadcrumbs from server
        let breadcrumbs: BreadcrumbItem[] = [];

        if (folderId) {
          // For subfolders, get breadcrumb directly
          const breadcrumbsResponse = await service.getFolderBreadcrumb(
            orgFprint,
            folderId,
          );
          breadcrumbs = breadcrumbsResponse || [];
        } else {
          // For root level, get root info by fetching breadcrumb from any first-level folder
          const rootFolders = foldersResponse.folders || [];
          if (rootFolders.length > 0 && rootFolders[0]) {
            try {
              const firstFolderBreadcrumb = await service.getFolderBreadcrumb(
                orgFprint,
                rootFolders[0].id,
              );
              // Extract just the root breadcrumb (first item)
              if (firstFolderBreadcrumb && firstFolderBreadcrumb.length > 0) {
                const rootBreadcrumb = firstFolderBreadcrumb[0];
                if (rootBreadcrumb) {
                  breadcrumbs = [rootBreadcrumb];
                }
              }
            } catch (error) {
              // Fallback to a basic root breadcrumb if API call fails
              breadcrumbs = [{ id: 0, name: 'root', type: 'folder' as const }];
            }
          } else {
            // No folders found, use fallback
            breadcrumbs = [{ id: 0, name: 'root', type: 'folder' as const }];
          }
        }

        return {
          folders: foldersResponse.folders || [],
          documents: documentsResponse.documents || [],
          next_page_token:
            documentsResponse.next_page_token ||
            foldersResponse.next_page_token,
          breadcrumbs,
        };
      } catch (error) {
        return handleApiError(error, 'getFolderContents');
      }
    },

    /**
     * Helper methods for easy access (following API spec patterns)
     */

    // Get root folders (omits parent_id)
    getRootFolders: async (
      orgFprint: string,
      options: { limit?: number; matterId?: number; pageToken?: string } = {},
    ): Promise<ClioApiResponse<ClioFolder>> => {
      return clioDocumentPickerService().listFolders(orgFprint, {
        limit: options.limit || 50,
        matter_id: options.matterId,
        page_token: options.pageToken,
        // Deliberately omit parent_id for root folders
      });
    },

    // Get subfolder contents (includes parent_id)
    getSubfolderContents: async (
      orgFprint: string,
      parentFolderId: number,
      options: { limit?: number; matterId?: number; pageToken?: string } = {},
    ): Promise<ClioApiResponse<ClioFolder>> => {
      return clioDocumentPickerService().listFolders(orgFprint, {
        limit: options.limit || 50,
        parent_id: parentFolderId,
        matter_id: options.matterId,
        page_token: options.pageToken,
      });
    },

    // Get documents in folder
    getDocumentsInFolder: async (
      orgFprint: string,
      folderId: number | null,
      options: {
        limit?: number;
        matterId?: number;
        pageToken?: string;
        onlyFullyUploaded?: boolean;
      } = {},
    ): Promise<ClioApiResponse<ClioDocument>> => {
      const params: DocumentSearchParams = {
        limit: options.limit || 50,
        matter_id: options.matterId,
        page_token: options.pageToken,
        only_fully_uploaded: options.onlyFullyUploaded ?? true,
      };

      // Only include parent_id if folderId is provided
      if (folderId) {
        params.parent_id = folderId;
      }

      return clioDocumentPickerService().listDocuments(orgFprint, params);
    },
  };
}

export default clioDocumentPickerService();
